import { mapGetters, mapState } from 'vuex';

import channelIconMixin from '@mixin/channelIcons';

import { isValueExist, noValueExist } from '@/utils/utils';
import { FILTER_KEYS } from '@/utils/consts';
import { capitalize } from '@util';
import { getChannelFilterName, getChannelName } from '@/utils/channelName';

import { refineAccountName } from '@/components/Dashboard/common/SearchBar/filters/helper';

import { refineRawText } from '@/utils/translator';
import { getFieldLocale } from '@/utils/field';
import {
    refineChannel,
    refineChannelIconName,
    refineSourceName,
    getSavedFilterGroupId,
} from '@/utils/dashboard';
import { refineChannelLogoSrc } from '@/utils/account';

const sortByChannel = R.sortBy(R.prop('channel'));

const {
    CHANNEL,
    AD_ACCOUNT_ID,
    LABEL,
    OBJECTIVE,
    GENERIC,
    STATUS,
    OS,
    PLATFORM,
} = FILTER_KEYS;

const checkIsFreeFormat = (account) => {
    return account?.isFreeFormat ?? false;
};

const formatString = R.compose(
    R.join(' '),
    R.map(capitalize),
    R.split(' '),
    R.toLower(),
    R.replace(/_/g, ' '),
    R.replace(/([a-z])([A-Z])/g, '$1 $2')
);

export default {
    data() {
        return {
            adAccountList: [],
        };
    },
    mixins: [channelIconMixin],
    computed: {
        ...mapState('newDashboard', ['tableAdAccounts']),
        ...mapGetters('newDashboard', [
            'tableAdAccountsByGroupId',
            'tableAdAccountsByChannelId',
            'tableAdAccountsByAlternativeChannelName',
            'dimensionById',
            'adAccounts',
        ]),
        /**
         * `tableAdAccountsByGroupId` keys on the raw `account.groupId`, which is
         * right for every account the filter dropdown stores as-is but misses
         * the mapped ones -- those are offered, and saved, under `marketing`.
         * Index by `getSavedFilterGroupId` instead, then fold in the blended
         * accounts that the marketing group also lists
         * (`filterWidgetOptions/dropdownOptions`
         * `conditionAccountsIgnoringScope`).
         */
        tableAdAccountsByFilterGroupId() {
            const { tableAdAccounts } = this;

            const accountsByGroupId = R.groupBy(
                getSavedFilterGroupId,
                tableAdAccounts || []
            );
            const blendedAccounts = R.filter(
                (acc) => R.pathEq(['settings', 'isFullJoin'], true, acc),
                tableAdAccounts || []
            );

            if (!blendedAccounts.length) {
                return accountsByGroupId;
            }

            return {
                ...accountsByGroupId,
                marketing: R.concat(
                    R.propOr([], 'marketing', accountsByGroupId),
                    blendedAccounts
                ),
            };
        },
    },
    watch: {
        tableAdAccounts: {
            handler(tableAdAccounts) {
                this.refineAdAccounts(tableAdAccounts);
            },
            deep: true,
            immediate: true,
        },
    },
    methods: {
        refineAdAccounts(tableAdAccounts) {
            const getRefinedName = refineAccountName.bind(this);
            const dataSourceList = (tableAdAccounts || []).map((asset) => {
                const refinedName = getRefinedName(asset);

                return {
                    ...asset,
                    name: refinedName,
                };
            });

            const sortedAdAccountList = [...sortByChannel(dataSourceList)];
            const _adAccountList = Object.freeze(sortedAdAccountList);

            this.adAccountList = _adAccountList;
        },
        fieldTranslator(field) {
            return this.$t(`FILTERS.${field}`);
        },
        getLevelTranslator(field) {
            switch (field) {
                case GENERIC:
                    return this.genericTranslator;
                default:
                    return (level) => this.levelTranslator(field, level);
            }
        },
        levelTranslator(field, level) {
            return this.$t(`FILTERS.${field}_option_${level}`);
        },
        genericTranslator(level) {
            return (
                this.genericBreakdownTranslator?.(level) ||
                getFieldLocale.call(this, level)
            );
        },
        operatorTranslator(value) {
            if (noValueExist(value)) return '';

            return this.$t(`FILTERS.operator_${value}`);
        },
        getValueTranslator(field, groupSetAccounts) {
            switch (field) {
                case OBJECTIVE:
                case STATUS:
                case OS: {
                    return (value) => this.valueTranslator(field, value);
                }
                case CHANNEL:
                case PLATFORM: {
                    return (value) => {
                        const adAccount =
                            this.tableAdAccountsByAlternativeChannelName[
                                value
                            ];
                        const displayName = adAccount
                            ? this.userLang === 'ko'
                                ? adAccount.displayChannelNameKR ||
                                  adAccount.displayChannelName
                                : adAccount.displayChannelName
                            : '';

                        return getChannelFilterName.call(
                            this,
                            value,
                            displayName ||
                                getChannelName.call(
                                    this,
                                    value,
                                    refineRawText(value)
                                )
                        );
                    };
                }
                case AD_ACCOUNT_ID: {
                    const groupSetAccountsByChannelId = R.indexBy(
                        R.prop('channelId'),
                        groupSetAccounts ?? []
                    );

                    return (channelId) => {
                        const account =
                            groupSetAccountsByChannelId[channelId] ||
                            this.tableAdAccountsByChannelId[channelId] ||
                            {};

                        if (!isValueExist(account)) return channelId;

                        return refineSourceName(account);
                    };
                }
                // only label valueTranslator need second arg
                case LABEL: {
                    const { dimensionById } = this;
                    return (labelId, dimensionId) => {
                        const dimension = R.propOr(
                            {},
                            dimensionId,
                            dimensionById
                        );

                        const { labels = [], name: dimensionName = '' } =
                            dimension;
                        const label =
                            R.find(({ id }) => id == labelId, labels) ?? {};

                        const labelName = R.propOr('', 'name', label);
                        return `${labelName} | ${dimensionName}`;
                    };
                }
                default: {
                    return (value) => value;
                }
            }
        },
        valueTranslator(field, value) {
            const lowerValue = value?.toLowerCase();

            return (
                value &&
                this.translateOr(
                    `FILTERS.${field}_option_${lowerValue}`,
                    formatString(value)
                )
            );
        },
        getValueLogoTranslator(field, groupSetAccounts) {
            switch (field) {
                case AD_ACCOUNT_ID: {
                    const groupSetAccountsByChannelId = R.indexBy(
                        R.prop('channelId'),
                        groupSetAccounts ?? []
                    );

                    return (channelId) => {
                        const account =
                            groupSetAccountsByChannelId[channelId] ||
                            this.tableAdAccountsByChannelId[channelId] ||
                            {};

                        if (!isValueExist(account)) return null;

                        const src = refineChannelLogoSrc(account);

                        return src ? (
                            <img src={src} />
                        ) : (
                            this.channelIconWithAdverity(refineChannel(account))
                        );
                    };
                }
                case CHANNEL:
                case PLATFORM: {
                    return (channel) => {
                        return this.channelIconWithAdverity(
                            refineChannelIconName(channel)
                        );
                    };
                }
                default:
                    undefined;
            }
        },
        groupTranslator(groupId) {
            if (groupId === 'marketing' || groupId === 'organic') {
                return this.$t(`FILTERS.group_set_${groupId}`);
            }

            const accounts = R.propOr(
                [],
                groupId,
                this.tableAdAccountsByGroupId
            );
            const account = R.head(accounts) ?? {};

            if (!checkIsFreeFormat(account)) {
                return (
                    (this.userLang === 'ko'
                        ? account.displayChannelNameKR ||
                          account.displayChannelName
                        : account.displayChannelName) || refineRawText(groupId)
                );
            }

            return refineSourceName(account);
        },
    },
};
